import React from 'react';

import type {
  ILivestockSubStepProps,
  SellOffFrequency,
} from '../productionInfo.types';
import {
  PRODUCT_GRADING_OPTIONS,
  SELL_OFF_FREQUENCY_OPTIONS,
  lshDescribeSellOffPeriod,
} from '../productionInfo.utils';

export const LivestockSubStep: React.FC<ILivestockSubStepProps> = ({
  livestockError,
  isLoadingLivestock,
  isSavingLivestock,
  livestockForm,
  livestockSelectOptions,
  livestockTypeOptions,
  sellOffFrequency,
  sellOffMonth,
  onceOffMonths,
  customMonths,
  customSellOffValues,
  livestockRecords,
  onLivestockFormChange,
  onSellOffFrequencyChange,
  onSellOffMonthChange,
  onCustomSellOffValueChange,
  onAddLivestockRecord,
  onRemoveLivestockRecord,
}) => (
  <div className="application-card">
    <div className="subform-stack">
      <header className="subform-stack__header">
        <h3>Livestock</h3>
        <p>Add livestock groups, quantities, grading and sell-off details.</p>
      </header>

      {livestockError ? (
        <div className="form-alert is-error">{livestockError}</div>
      ) : null}

      {isLoadingLivestock ? (
        <div className="application-empty-state">
          <h3>Loading livestock records</h3>
        </div>
      ) : null}

      <div className="form-grid two-columns">
        <div className="form-field">
          <label className="form-label" htmlFor="livestock-item">
            Livestock
          </label>
          <select
            id="livestock-item"
            value={livestockForm.livestockId}
            onChange={onLivestockFormChange('livestockId')}
          >
            <option value="">Select livestock</option>
            {livestockSelectOptions.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="livestock-quantity">
            Quantity
          </label>
          <input
            id="livestock-quantity"
            type="number"
            min="0"
            value={livestockForm.quantity}
            onChange={onLivestockFormChange('quantity')}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="livestock-type">
            Type
          </label>
          <select
            id="livestock-type"
            value={livestockForm.type}
            onChange={onLivestockFormChange('type')}
          >
            <option value="">Select type</option>
            {livestockTypeOptions.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="livestock-grade">
            Product grading
          </label>
          <select
            id="livestock-grade"
            value={livestockForm.productGrading}
            onChange={onLivestockFormChange('productGrading')}
          >
            <option value="">Select grading</option>
            {PRODUCT_GRADING_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="livestock-selloff-frequency">
            Sell-off frequency
          </label>
          <select
            id="livestock-selloff-frequency"
            value={sellOffFrequency}
            onChange={(event) =>
              onSellOffFrequencyChange(event.target.value as SellOffFrequency)
            }
          >
            {SELL_OFF_FREQUENCY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="livestock-price">
            Price
          </label>
          <input
            id="livestock-price"
            type="number"
            min="0"
            value={livestockForm.price}
            onChange={onLivestockFormChange('price')}
          />
        </div>
      </div>

      {sellOffFrequency === 'once-off' ? (
        <div className="form-field">
          <label className="form-label" htmlFor="livestock-selloff-month">
            Once-off sell month
          </label>
          <select
            id="livestock-selloff-month"
            value={sellOffMonth}
            onChange={(event) => onSellOffMonthChange(event.target.value)}
          >
            <option value="">Select month</option>
            {onceOffMonths.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {sellOffFrequency === 'custom' ? (
        <div className="form-field">
          <label className="form-label">
            Custom monthly sell-off quantities
          </label>
          <div className="selloff-month-grid">
            {customMonths.map((month) => (
              <label key={month.key} className="selloff-month-grid__item">
                <span>{month.label}</span>
                <input
                  type="number"
                  min="0"
                  value={customSellOffValues[month.key] ?? ''}
                  onChange={(event) =>
                    onCustomSellOffValueChange(month.key, event.target.value)
                  }
                />
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <div className="form-field">
        <label className="form-label" htmlFor="livestock-notes">
          Notes
        </label>
        <textarea
          id="livestock-notes"
          rows={3}
          value={livestockForm.notes}
          onChange={onLivestockFormChange('notes')}
        />
      </div>

      <div className="subform-stack__actions">
        <button
          type="button"
          className="money-btn money-btn--secondary money-btn--sm"
          onClick={onAddLivestockRecord}
          disabled={isLoadingLivestock || isSavingLivestock}
        >
          Add livestock entry
        </button>
        <p>{livestockRecords.length} entries prepared</p>
      </div>

      {livestockRecords.length > 0 ? (
        <ul className="simple-record-list">
          {livestockRecords.map((record, index) => (
            <li key={`${record.id ?? 'draft'}-${index}`}>
              <div>
                <strong>{record.livestock?.name || 'Livestock item'}</strong>
                <p>
                  Qty: {record.quantity ?? 0} | Grade:{' '}
                  {record.productGrading || '-'} | Sell-off:{' '}
                  {lshDescribeSellOffPeriod(record.sellOfPeriod)}
                </p>
              </div>
              <button
                type="button"
                className="money-btn money-btn--danger money-btn--sm"
                onClick={() => {
                  void onRemoveLivestockRecord(record);
                }}
                disabled={isSavingLivestock}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  </div>
);
